"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.avatarOpenApiDocs = exports.AvatarResponse = void 0;
exports.fileObject = fileObject;
function fileObject(description, required = false, nullable = false) {
    return {
        type: 'object',
        description,
        properties: {
            filename: {
                type: 'string',
                description: 'Generated filename for the uploaded file',
                example: 'avatar_logo_123.png'
            },
            originalName: {
                type: 'string',
                description: 'Original filename from user upload',
                example: 'my-avatar-logo.png'
            },
            mimeType: {
                type: 'string',
                description: 'MIME type of the uploaded file',
                example: 'image/png'
            },
            size: {
                type: 'integer',
                description: 'File size in bytes',
                example: 15420
            },
            path: {
                type: 'string',
                description: 'Base64 encoded file content (without data: prefix)',
                example: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADU...'
            }
        },
        required,
        nullable
    };
}
const AvatarCreatePayload = {
    type: 'object',
    description: 'Avatar creation request parameters',
    properties: {
        name: {
            type: 'string',
            description: 'Avatar name - a descriptive name for the AI avatar that users will interact with',
            example: 'Customer Support Bot',
            required: true
        },
        description: {
            type: 'string',
            description: 'Detailed description of the avatar\'s purpose, personality, and capabilities',
            example: 'A helpful customer support avatar specialized in product inquiries and technical assistance',
            required: true
        },
        model: {
            type: 'object',
            description: 'AI model configuration specifying which language model to use for this avatar',
            properties: {
                provider: {
                    type: 'object',
                    properties: {
                        name: {
                            type: 'string',
                            description: 'AI model provider (e.g., "openai", "anthropic", "azure")',
                            example: 'OpenAI'
                        },
                        value: {
                            type: 'string',
                            description: 'Value associated with the AI model provider',
                            example: 'openai'
                        }
                    }
                },
                model: {
                    type: 'object',
                    properties: {
                        cost: {
                            type: 'number',
                        },
                        label: {
                            type: 'string',
                        },
                        value: {
                            type: 'string'
                        },
                    }
                }
            },
            required: false
        },
        systemPrompt: {
            type: 'string',
            description: 'System prompt that defines the avatar\'s behavior, personality, and response style. This guides how the AI responds to user interactions.',
            example: 'You are a friendly and professional customer support representative. Always be helpful, patient, and provide accurate information.',
            required: false
        },
        gender: {
            type: 'string',
            enum: ['male', 'female', 'other'],
            description: 'Avatar gender identity for personalization and voice selection',
            example: 'female',
            required: false
        },
        age: {
            type: 'integer',
            minimum: 1,
            maximum: 100,
            description: 'Avatar age in years, used for personality and communication style customization',
            example: 28,
            required: false
        },
        voice: {
            type: 'string',
            description: 'Voice identifier for text-to-speech functionality when isVoiceAgent is true',
            example: 'alloy',
            required: false
        },
        isVoiceAgent: {
            type: 'boolean',
            description: 'Whether this avatar supports voice conversations through ElevenLabs integration. Enables real-time voice chat capabilities.',
            default: false,
            required: false
        },
        logo: fileObject('Avatar logo/image file upload data in base64 format', false, true),
        knowledgeIds: {
            type: 'array',
            description: 'Array of knowledge base IDs to associate with this avatar. The avatar will use these knowledge sources to provide informed responses.',
            items: {
                type: 'string',
                description: 'Knowledge base identifier'
            },
            example: ['kb_001', 'kb_002', 'kb_003'],
            required: false
        },
        fullKnowledgeIds: {
            type: 'array',
            description: 'Array of full knowledge base IDs for full file knowledge integration (Dont use RAG on those during query - paste in WHOLE FILE)',
            items: {
                type: 'string',
                description: 'Full knowledge base identifier'
            },
            example: ['full_kb_001', 'full_kb_002'],
            required: false
        },
        lang: {
            type: 'string',
            description: 'Primary language code for the avatar (ISO 639-1 format). Used for voice agent creation and localization.',
            example: 'en',
            default: 'en',
            required: false
        },
        common: {
            type: 'boolean',
            description: 'Whether this avatar is available to all users (system-wide). Only super admins can set this flag.',
            default: false,
            required: false
        },
        active: {
            type: 'boolean',
            description: 'Whether the avatar is currently active and available for use',
            default: true,
            required: false
        },
        enabledTools: {
            type: 'array',
            description: 'Array of enabled tool names that this avatar can use during conversations',
            items: {
                type: 'string',
                description: 'Tool name identifier'
            },
            example: ['web_search', 'calculator', 'file_reader'],
            required: false
        },
        webhookDefinitions: {
            type: 'array',
            description: 'Array of webhook definitions for event notifications and scheduled tasks',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier for the webhook definition',
                        example: 'wh_123e4567-e89b-12d3-a456-426614174000'
                    },
                    name: {
                        type: 'string',
                        description: 'Human-readable name for the webhook',
                        example: 'Conversation Analysis Webhook'
                    },
                    address: {
                        type: 'string',
                        description: 'Target URL endpoint for webhook notifications',
                        example: 'https://api.example.com/webhooks/avatar-events'
                    },
                    eventType: {
                        type: 'string',
                        description: 'Type of event that triggers this webhook',
                        enum: ['conversation_analysis', 'avatar_updated', 'knowledge_added', 'knowledge_removed'],
                        example: 'conversation_analysis'
                    },
                    cronEnabled: {
                        type: 'boolean',
                        description: 'Whether this webhook should be triggered on a schedule',
                        default: false
                    },
                    cronInterval: {
                        type: 'string',
                        description: 'Cron expression for scheduled webhook execution (when cronEnabled is true)',
                        example: '*/30 * * * *'
                    },
                    active: {
                        type: 'boolean',
                        description: 'Whether this webhook definition is currently active',
                        default: true
                    },
                    authorizationEnabled: {
                        type: 'boolean',
                        description: 'Whether to include authorization headers in webhook requests',
                        default: false
                    },
                    authorizationHeader: {
                        type: 'string',
                        description: 'Authorization header name (when authorizationEnabled is true)',
                        example: 'Authorization'
                    },
                    authorizationKey: {
                        type: 'string',
                        description: 'Authorization header value (when authorizationEnabled is true)',
                        example: 'Bearer your-api-token'
                    }
                }
            },
            required: false
        },
        avatarInstructions: {
            type: 'array',
            description: 'Array of avatar instruction layers defining behavior, personality traits, or knowledge domains. Each instruction can optionally include a file attachment and versioning metadata.',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier for the instruction (db id for existing entries)',
                        example: 'instr_123e4567-e89b-12d3-a456-426614174000'
                    },
                    layer: {
                        type: 'string',
                        description: 'Instruction layer name categorizing the type of instruction (e.g., "personality", "knowledge", "rules")',
                        example: 'personality'
                    },
                    color: {
                        type: 'string',
                        description: 'Color code for visual identification of the instruction layer in the UI',
                        example: '#FF5733'
                    },
                    notes: {
                        type: 'string',
                        description: 'Additional notes or comments about this instruction layer',
                        example: 'Main personality traits for customer interactions'
                    },
                    instructionPrompt: {
                        type: 'string',
                        description: 'The instruction prompt text that guides the avatar behavior for this layer',
                        example: 'Always respond in a friendly and professional manner. Use simple language.'
                    },
                    file: fileObject('Required file attachment for this instruction (existing file reference or base64-encoded upload)'),
                    isChanged: {
                        type: 'boolean',
                        description: 'Flag indicating whether this instruction has been modified (used for partial updates) - setting this true adds new version to instruction entry',
                        default: false
                    },
                    isPublic: {
                        type: 'boolean',
                        description: 'Flag indicating whether this instruction is public (will be sent in webhook requests)',
                        default: false
                    }
                }
            },
            required: false
        },
        lexicon: {
            type: 'array',
            description: 'Array of lexicon entries for custom vocabulary and terminology definitions used by the avatar',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier for the lexicon entry',
                        example: 'lex_001'
                    },
                    label: {
                        type: 'string',
                        description: 'Display label for the lexicon entry',
                        example: 'AI Assistant'
                    },
                    value: {
                        type: 'string',
                        description: 'The actual value or definition for the lexicon entry',
                        example: 'An artificial intelligence assistant designed to help users'
                    },
                    hidden: {
                        type: 'boolean',
                        description: 'Whether this lexicon entry is hidden from public view',
                        default: false
                    }
                },
                required: ['id', 'label', 'value', 'hidden']
            },
            required: false
        }
    },
    required: ['name', 'description']
};
const AvatarUpdatePayload = AvatarCreatePayload;
const AvatarInstructionVersionResponse = {
    type: 'object',
    description: 'A snapshot of an avatar instruction at a specific version',
    properties: {
        id: {
            type: 'number',
            description: 'Unique identifier of the instruction version'
        },
        layer: {
            type: 'string',
            description: 'Instruction layer name'
        },
        color: {
            type: 'string',
            description: 'Color code for visual identification of the instruction layer'
        },
        notes: {
            type: 'string',
            description: 'Additional notes or comments about this version'
        },
        instructionPrompt: {
            type: 'string',
            description: 'The instruction prompt text for this version'
        },
        file: fileObject('File attachment associated with this version', true, false),
        versionNumber: {
            type: 'integer',
            description: 'Sequential version number'
        }
    }
};
exports.AvatarResponse = {
    type: 'object',
    description: 'Avatar object',
    properties: {
        id: {
            type: 'number',
            description: 'Avatar unique identifier'
        },
        name: {
            type: 'string',
            description: 'Avatar name'
        },
        description: {
            type: 'string',
            description: 'Avatar description'
        },
        gender: {
            type: 'string',
            enum: ['male', 'female', 'other'],
            description: 'Avatar gender identity'
        },
        age: {
            type: 'integer',
            description: 'Avatar age in years'
        },
        voice: {
            type: 'string',
            description: 'Voice identifier for text-to-speech'
        },
        image: {
            type: 'string',
            description: 'Avatar image URL'
        },
        logo: fileObject('Avatar logo image', false, true),
        model: {
            type: 'object',
            description: 'AI model configuration',
            properties: {
                provider: {
                    type: 'object',
                    properties: {
                        name: { type: 'string', description: 'Provider name' },
                        value: { type: 'string', description: 'Provider value' }
                    }
                },
                model: {
                    type: 'object',
                    properties: {
                        label: { type: 'string' },
                        value: { type: 'string' },
                        cost: { type: 'number' }
                    }
                }
            }
        },
        systemPrompt: {
            type: 'string',
            description: 'System prompt defining the avatar behavior'
        },
        owner: {
            type: 'string',
            description: 'ID of the owning user'
        },
        creator: {
            type: 'object',
            description: 'User who created the avatar',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                username: { type: 'string' },
                email: { type: 'string' }
            }
        },
        common: {
            type: 'boolean',
            description: 'Whether this avatar is available system-wide'
        },
        active: {
            type: 'boolean',
            description: 'Whether the avatar is active'
        },
        isVoiceAgent: {
            type: 'boolean',
            description: 'Whether this avatar supports voice conversations'
        },
        elevenLabsAgentId: {
            type: 'string',
            description: 'ElevenLabs agent ID for voice integration'
        },
        processing: {
            type: 'boolean',
            description: 'Whether the avatar is currently processing (e.g. knowledge indexing)'
        },
        knowledgeIds: {
            type: 'array',
            description: 'IDs of knowledge entries associated with this avatar',
            items: { type: 'string' }
        },
        fullKnowledgeIds: {
            type: 'array',
            description: 'IDs of full-file knowledge entries (not chunked via RAG)',
            items: { type: 'string' }
        },
        enabledTools: {
            type: 'array',
            description: 'Array of enabled tool names for this avatar',
            items: {
                type: 'string'
            }
        },
        ragSettings: {
            type: 'object',
            description: 'RAG (Retrieval Augmented Generation) configuration',
            properties: {
                chunkSize: { type: 'integer' },
                chunkOverlap: { type: 'integer' },
                separators: { type: 'array', items: { type: 'string' } },
                defaultMaxResults: { type: 'integer' },
                maxResults: { type: 'integer' },
                similarityThreshold: { type: 'number' }
            }
        },
        webhookDefinitions: {
            type: 'array',
            description: 'Array of webhook definitions configured for this avatar',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier for the webhook definition'
                    },
                    name: {
                        type: 'string',
                        description: 'Human-readable name for the webhook'
                    },
                    address: {
                        type: 'string',
                        description: 'Target URL endpoint for webhook notifications'
                    },
                    eventType: {
                        type: 'string',
                        description: 'Type of event that triggers this webhook',
                        enum: ['conversation_analysis', 'avatar_updated', 'knowledge_notify', 'avatar_test_complete']
                    },
                    cronEnabled: {
                        type: 'boolean',
                        description: 'Whether this webhook is triggered on a schedule'
                    },
                    cronInterval: {
                        type: 'string',
                        description: 'Cron expression for scheduled webhook execution'
                    },
                    active: {
                        type: 'boolean',
                        description: 'Whether this webhook definition is currently active'
                    },
                    authorizationEnabled: {
                        type: 'boolean',
                        description: 'Whether authorization headers are included in webhook requests'
                    },
                    authorizationHeader: {
                        type: 'string',
                        description: 'Authorization header name'
                    },
                    authorizationKey: {
                        type: 'string',
                        description: 'Authorization header value'
                    }
                }
            }
        },
        avatarInstructions: {
            type: 'array',
            description: 'Array of avatar instruction layers with their version history',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier of the avatar instruction'
                    },
                    versions: {
                        type: 'array',
                        description: 'All versions of this instruction, ordered by version number',
                        items: AvatarInstructionVersionResponse
                    },
                    lastVersion: {
                        ...AvatarInstructionVersionResponse,
                        description: 'The most recent version of this instruction',
                        nullable: true
                    }
                }
            }
        },
        lexicon: {
            type: 'array',
            description: 'Array of lexicon entries for custom vocabulary and terminology definitions',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Unique identifier for the lexicon entry'
                    },
                    label: {
                        type: 'string',
                        description: 'Display label for the lexicon entry'
                    },
                    value: {
                        type: 'string',
                        description: 'The actual value or definition for the lexicon entry'
                    },
                    hidden: {
                        type: 'boolean',
                        description: 'Whether this lexicon entry is hidden from public view'
                    }
                }
            }
        },
        created_at: {
            type: 'string',
            format: 'date-time'
        },
        updated_at: {
            type: 'string',
            format: 'date-time'
        }
    }
};
const AvatarListDesc = {
    description: 'List all avatars with filtering and pagination',
    payload: {
        query: {
            type: 'object',
            properties: {
                p: {
                    type: 'integer',
                    description: 'Page number (1-indexed)',
                    minimum: 1,
                    default: 1
                },
                per_page: {
                    type: 'integer',
                    description: 'Number of items per page',
                    minimum: 1,
                    maximum: 100,
                    default: 20
                },
                all: {
                    type: 'integer',
                    description: 'Return all results without pagination (1 for true, 0 for false)',
                    enum: [0, 1]
                }
            }
        }
    },
    responses: {
        200: {
            description: 'List of avatars',
            returnParams: {
                type: 'object',
                properties: {
                    success: { type: 'boolean', example: true },
                    data: {
                        type: 'array',
                        items: exports.AvatarResponse
                    },
                    count: {
                        type: 'integer',
                        description: 'Total number of avatars matching the query'
                    }
                }
            }
        },
        400: {
            description: 'Bad request',
            returnParams: {
                type: 'object',
                properties: {
                    success: { type: 'boolean', example: false },
                    error: { type: 'string' },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling'
                    }
                }
            }
        },
        500: {
            description: 'Failed to fetch avatars',
            returnParams: {
                type: 'object',
                properties: {
                    success: { type: 'boolean', example: false },
                    error: { type: 'string' },
                    error_code: {
                        type: 'integer',
                        description: 'Error code for programmatic handling'
                    }
                }
            }
        }
    }
};
exports.avatarOpenApiDocs = {
    'avatar.instruction.versions': {
        description: 'Get version history for a specific avatar instruction layer',
        responses: {
            200: {
                description: 'Instruction version history retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'array',
                            description: 'List of instruction versions ordered by version number',
                            items: {
                                type: 'object',
                                properties: {
                                    id: {
                                        type: 'number',
                                        description: 'Unique identifier of the instruction version'
                                    },
                                    layer: {
                                        type: 'string',
                                        description: 'Instruction layer name'
                                    },
                                    color: {
                                        type: 'string',
                                        description: 'Color code for visual identification of the instruction layer'
                                    },
                                    notes: {
                                        type: 'string',
                                        description: 'Additional notes or comments about this version'
                                    },
                                    instructionPrompt: {
                                        type: 'string',
                                        description: 'The instruction prompt text for this version'
                                    },
                                    file: fileObject('File attachment associated with this version'),
                                    versionNumber: {
                                        type: 'integer',
                                        description: 'Sequential version number'
                                    }
                                }
                            }
                        }
                    }
                }
            },
            500: {
                description: 'Failed to fetch instruction versions',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        data: { type: 'null' },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1016
                        }
                    }
                }
            }
        }
    },
    'avatar.instruction.revert': {
        description: 'Revert an avatar instruction to a specific previous version. Restores the instruction layer, prompt, color, notes, and file from the selected version.',
        responses: {
            200: {
                description: 'Instruction reverted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'object',
                            description: 'The reverted instruction version data',
                            properties: {
                                id: {
                                    type: 'number',
                                    description: 'Unique identifier of the instruction version'
                                },
                                layer: {
                                    type: 'string',
                                    description: 'Instruction layer name'
                                },
                                color: {
                                    type: 'string',
                                    description: 'Color code for visual identification'
                                },
                                notes: {
                                    type: 'string',
                                    description: 'Additional notes about this version'
                                },
                                instructionPrompt: {
                                    type: 'string',
                                    description: 'The instruction prompt text'
                                },
                                file: {
                                    type: 'object',
                                    description: 'File attachment associated with this version',
                                    nullable: true
                                },
                                versionNumber: {
                                    type: 'integer',
                                    description: 'Sequential version number'
                                }
                            }
                        }
                    }
                }
            },
            500: {
                description: 'Failed to revert instruction version',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        data: { type: 'null' },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1016
                        }
                    }
                }
            }
        }
    },
    'avatar.knowledge': {
        description: 'Get avatar knowledge base information',
        responses: {
            200: {
                description: 'Avatar knowledge retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'object',
                            properties: {
                                knowledge: {
                                    type: 'array',
                                    items: {
                                        type: 'string'
                                    }
                                }
                            }
                        }
                    }
                }
            },
            404: {
                description: 'Avatar not found',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            }
        }
    },
    'avatar.processing': {
        description: 'Get avatar processing status',
        responses: {
            200: {
                description: 'Avatar processing status retrieved successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'object',
                            properties: {
                                status: {
                                    type: 'string'
                                },
                                progress: {
                                    type: 'number'
                                }
                            }
                        }
                    }
                }
            }
        }
    },
    'avatar.list': AvatarListDesc,
    'avatar.create': {
        description: 'Create a new AI avatar with custom configuration and knowledge base integration',
        payload: {
            body: AvatarCreatePayload
        },
        responses: {
            200: {
                description: 'Avatar created successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.AvatarResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or missing required fields',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1012
                        }
                    }
                }
            },
            403: {
                description: 'Forbidden - avatar limit exceeded for account grade',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1003
                        }
                    }
                }
            },
            404: {
                description: 'Avatar instruction version not found',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: {
                            type: 'string',
                            example: 'The requested avatar instruction version could not be found'
                        },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1016
                        }
                    }
                }
            },
            422: {
                description: 'No file provided for avatar instruction',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: {
                            type: 'string',
                            example: 'No file provided for avatar instruction'
                        },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1017
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error - avatar creation failed',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1004
                        }
                    }
                }
            }
        }
    },
    'avatar.get': {
        description: 'Get a single avatar by ID',
        responses: {
            200: {
                description: 'Avatar details',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.AvatarResponse
                    }
                }
            },
            404: {
                description: 'Avatar not found or access denied',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        data: { type: 'null' },
                        error: { type: 'string' }
                    }
                }
            },
            500: {
                description: 'Failed to fetch avatar',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling'
                        }
                    }
                }
            }
        }
    },
    'avatar.update': {
        description: 'Update an existing avatar\'s configuration, knowledge associations, or settings',
        payload: {
            body: AvatarUpdatePayload
        },
        responses: {
            200: {
                description: 'Avatar updated successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.AvatarResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or invalid parameters',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            },
            403: {
                description: 'Forbidden - insufficient permissions to update avatar',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            },
            404: {
                description: 'Avatar not found',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            },
            422: {
                description: 'No file provided for avatar instruction',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: {
                            type: 'string',
                            example: 'No file provided for avatar instruction'
                        },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1017
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error - avatar update failed',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1005
                        }
                    }
                }
            }
        }
    },
    'avatar.patch': {
        description: 'Update specific properties of an avatar by key-value pair. ',
        payload: {
            body: AvatarUpdatePayload
        },
        responses: {
            200: {
                description: 'Avatar property updated successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.AvatarResponse
                    }
                }
            },
            400: {
                description: 'Bad request - validation error or invalid property key',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1013
                        }
                    }
                }
            },
            403: {
                description: 'Forbidden - insufficient permissions to update avatar',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            },
            404: {
                description: 'Avatar not found',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1002
                        }
                    }
                }
            },
            500: {
                description: 'Internal server error - avatar property update failed',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                        error_code: {
                            type: 'integer',
                            description: 'Error code for programmatic handling',
                            example: 1013
                        }
                    }
                }
            }
        }
    },
    'avatar.delete': {
        description: 'Delete avatar by ID',
        responses: {
            200: {
                description: 'Avatar deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true }
                    }
                }
            },
            404: {
                description: 'Avatar not found',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' }
                    }
                }
            }
        }
    }
};
